import React, { useCallback, useMemo, useState } from "react";
import { RefreshCw } from "lucide-react";
import { clinicianPortalApi } from "../api/clinicianPortalApi";
import { PortalPatient, ValueEngagementAssignment } from "../api/contracts";
import { useActivityTemplateTypeLookup } from "../hooks/useActivityTemplateTypeLookup";
import { usePortalData } from "../hooks/usePortalData";
import { formatPortalDate } from "../utils/dateTime";
import { ActivityTemplateFilterField } from "./ActivityTemplateFilterField";
import {
  HomeworkStatusPill,
  PortalDataTable,
  PortalTableColumn,
} from "./DataDisplay";
import { EmptyState, ErrorState, LoadingState } from "./FeedbackStates";
import { ValueEngagementMetrics } from "./ReportVisuals";

export function ValueEngagementReport({
  clinicianCode,
  patientId,
  patients,
  onPatientChange,
}: {
  clinicianCode: string;
  patientId: string;
  patients: readonly PortalPatient[];
  onPatientChange: (patientId: string) => void;
}) {
  const headingId = React.useId();
  const clientFilterId = React.useId();
  const activityFilterId = React.useId();
  const activityTypeByTemplateId = useActivityTemplateTypeLookup(clinicianCode);
  const [activityTemplateId, setActivityTemplateId] = useState("");
  const request = useCallback(
    (signal: AbortSignal) =>
      clinicianPortalApi.getValueEngagement(
        clinicianCode,
        patientId,
        activityTemplateId,
        signal,
      ),
    [activityTemplateId, clinicianCode, patientId],
  );
  const state = usePortalData(request, { enabled: Boolean(patientId) });
  const responseData =
    state.status === "success" || state.status === "refreshing"
      ? state.data
      : undefined;
  const selectedFilter = activityTemplateId || null;
  const data =
    responseData?.filters.activityTemplateId === selectedFilter
      ? responseData
      : undefined;
  const available = Boolean(
    data?.found && data.authorized && data.therapist && data.client,
  );
  const activityOptions = responseData?.activityOptions ?? [];

  const columns = useMemo<
    readonly PortalTableColumn<ValueEngagementAssignment>[]
  >(
    () => [
      {
        key: "access-code",
        header: "Access Code",
        initialWidth: 135,
        resizable: false,
        render: (assignment) => <strong>{assignment.accessCode}</strong>,
      },
      {
        key: "homework-activity",
        header: "Homework Activity",
        initialWidth: 265,
        render: (assignment) => assignment.homeworkActivity,
      },
      {
        key: "value-supported",
        header: "Value Supported",
        initialWidth: 215,
        render: (assignment) =>
          assignment.valueSupportedName || (
            <span className="cp-empty-value">No linked value</span>
          ),
      },
      {
        key: "completion-status",
        header: "Status",
        initialWidth: 205,
        resizable: false,
        render: (assignment) => (
          <HomeworkStatusPill status={assignment.completionStatus} />
        ),
      },
      {
        key: "assigned",
        header: "Assigned",
        initialWidth: 145,
        resizable: false,
        render: (assignment) => formatPortalDate(assignment.assignedAt),
      },
    ],
    [],
  );

  return (
    <section className="cp-value-engagement-report" aria-labelledby={headingId}>
      <div className="cp-value-engagement-report__header">
        <div>
          <h3 id={headingId}>Impact of Values on Homework Engagement</h3>
          <p>
            Compare terminal homework outcomes with and without a linked client
            value.
          </p>
        </div>
        <button
          type="button"
          className="cp-button cp-button--secondary"
          disabled={
            !patientId ||
            state.status === "loading" ||
            state.status === "refreshing"
          }
          onClick={state.refresh}
        >
          <RefreshCw aria-hidden="true" />
          {state.status === "refreshing" ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      <div className="cp-value-engagement-report__filters">
        <label htmlFor={clientFilterId}>
          <span>Client Access Code</span>
          <select
            id={clientFilterId}
            value={patientId}
            onChange={(event) => onPatientChange(event.target.value)}
          >
            <option value="">Select a client</option>
            {patients.map((patient) => (
              <option key={patient.id} value={patient.id}>
                {patient.accessCode} • {patient.nickname}
              </option>
            ))}
          </select>
        </label>
        <ActivityTemplateFilterField
          id={activityFilterId}
          value={activityTemplateId}
          options={activityOptions}
          activityTypeByTemplateId={activityTypeByTemplateId}
          disabled={!patientId || !activityOptions.length}
          onChange={setActivityTemplateId}
        />
      </div>

      {!patientId ? (
        <EmptyState
          title="Select a client"
          description="Choose a client access code to load the value engagement report."
        />
      ) : null}
      {patientId &&
        (state.status === "loading" ||
        (state.status === "refreshing" && !data) ? (
          <LoadingState label="Loading value engagement report" />
        ) : null)}
      {patientId && state.status === "error" ? (
        <ErrorState error={state.error} onRetry={state.refresh} />
      ) : null}
      {data && !available ? (
        <ErrorState
          error={new Error("Value engagement report is not available")}
        />
      ) : null}

      {available && data ? (
        <div className="cp-value-engagement-report__body">
          <ValueEngagementMetrics
            linked={{
              rate: data.metrics.valueLinked.engagementRate,
              engaged: data.metrics.valueLinked.engagedCount,
              sampleSize: data.metrics.valueLinked.sampleSize,
            }}
            unlinked={{
              rate: data.metrics.noValueLinked.engagementRate,
              engaged: data.metrics.noValueLinked.engagedCount,
              sampleSize: data.metrics.noValueLinked.sampleSize,
            }}
          />

          {data.assignments.length ? (
            <PortalDataTable
              columns={columns}
              records={data.assignments}
              getRecordKey={(assignment) => assignment.assignmentId}
              caption="Homework assignments included in the value engagement report"
            />
          ) : (
            <p className="cp-value-engagement-report__empty" role="status">
              No terminal homework outcomes match this filter yet.
            </p>
          )}
        </div>
      ) : null}
    </section>
  );
}
